import { Head, useForm } from '@inertiajs/react'
import { PageHeader, PageContent } from '@/components/PageHeader'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { ExportResult } from '@/types'
import { DownloadIcon, HardDriveIcon, FileSpreadsheetIcon, PackageIcon } from 'lucide-react'
import { cn } from '@/lib/utils'

interface Props {
  export_results: ExportResult[]
  accounting_start_date: string | null
  accounting_end_date: string | null
}

const statusColors = {
  pending: 'bg-warning/8 text-warning border-warning/15',
  processing: 'bg-primary/8 text-primary border-primary/15',
  completed: 'bg-success/8 text-success border-success/15',
  failed: 'bg-destructive/8 text-destructive border-destructive/15',
}

export default function Index({ export_results, accounting_start_date, accounting_end_date }: Props) {
  const { data, setData, post, processing } = useForm({
    start_date: accounting_start_date || '',
    end_date: accounting_end_date || '',
    new_label_date: '',
  })

  function handleAccounting(e: React.FormEvent) {
    e.preventDefault()
    post('/exports/accounting')
  }

  function handleStorageDownload() {
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = '/exports/storage'
    const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    if (csrf) {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = 'authenticity_token'
      input.value = csrf
      form.appendChild(input)
    }
    document.body.appendChild(form)
    form.submit()
    document.body.removeChild(form)
  }

  return (
    <>
      <Head title="Exports" />
      <PageHeader title="Exports" description="Download your financial data" />

      <PageContent className="space-y-5">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-primary/8 p-2">
                  <HardDriveIcon className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-[15px]">Storage Backup</CardTitle>
                  <CardDescription className="text-[13px]">
                    Download all receipts and attachments
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Button onClick={handleStorageDownload} variant="outline" size="sm" className="gap-1.5">
                <DownloadIcon className="h-3.5 w-3.5" />
                Download Storage
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-primary/8 p-2">
                  <FileSpreadsheetIcon className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-[15px]">Accounting Export</CardTitle>
                  <CardDescription className="text-[13px]">
                    Generate export for your accountant
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAccounting} className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label htmlFor="start_date" className="text-[13px] font-medium">
                      Start Date
                    </Label>
                    <Input
                      id="start_date"
                      type="date"
                      value={data.start_date}
                      onChange={(e) => setData('start_date', e.target.value)}
                      required
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label htmlFor="end_date" className="text-[13px] font-medium">
                      End Date
                    </Label>
                    <Input
                      id="end_date"
                      type="date"
                      value={data.end_date}
                      onChange={(e) => setData('end_date', e.target.value)}
                      required
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label htmlFor="new_label_date" className="text-[13px] font-medium">
                      New Label Date
                    </Label>
                    <Input
                      id="new_label_date"
                      type="date"
                      value={data.new_label_date}
                      onChange={(e) => setData('new_label_date', e.target.value)}
                      className="mt-1.5"
                    />
                  </div>
                </div>
                <Button type="submit" disabled={processing} size="sm" className="gap-1.5">
                  <DownloadIcon className="h-3.5 w-3.5" />
                  Generate Export
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-[15px]">Export History</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {export_results.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="rounded-full bg-muted p-3.5">
                  <PackageIcon className="h-6 w-6 text-muted-foreground" />
                </div>
                <h3 className="mt-3 text-[15px] font-medium text-foreground">No exports yet</h3>
                <p className="mt-1 text-[13px] text-muted-foreground">
                  Your export history will appear here.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="text-[13px] font-medium text-muted-foreground">Created</TableHead>
                    <TableHead className="text-[13px] font-medium text-muted-foreground">Date Range</TableHead>
                    <TableHead className="text-[13px] font-medium text-muted-foreground">Status</TableHead>
                    <TableHead className="text-[13px] font-medium text-muted-foreground">Download</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {export_results.map((result) => (
                    <TableRow key={result.id}>
                      <TableCell className="text-[13px] text-muted-foreground tabular-nums">
                        {result.created_at}
                      </TableCell>
                      <TableCell className="text-[13px] text-foreground tabular-nums">
                        {result.start_date} — {result.end_date}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={cn('capitalize text-[11px]', statusColors[result.status])}
                        >
                          {result.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {result.zip_url ? (
                          <a
                            href={result.zip_url}
                            className="inline-flex items-center gap-1.5 text-[13px] font-medium text-primary hover:text-primary/80 transition-colors"
                          >
                            <DownloadIcon className="h-3.5 w-3.5" />
                            Download
                          </a>
                        ) : (
                          <span className="text-[13px] text-muted-foreground">-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </PageContent>
    </>
  )
}
